/**
 * Member **Payment methods** tab (DNKADM-0252/DNKADM-0262) — the member's saved
 * cards on file, so the front desk can list, add, make default, replace and remove them. Backed
 * by `@/api/memberCards` on the bearer-only `/v2/admin/**` surface, loaded through
 * `useResource` like the other self-contained member tabs.
 *
 * **Saving a card moves NO money.** There is no charge and no authorization hold, and
 * nothing appears on the member's statement — the copy in the add form says so plainly.
 * The browser tokenizes through the shipped {@link CardPaymentField} (Square Web
 * Payments SDK); only the single-use nonce leaves the page, and it is never held in
 * state beyond the request. No PAN or CVV ever reaches a server.
 *
 * **The list is NEVER cached.** It lives in this component's `useResource` for exactly
 * as long as the tab is mounted, and it is refetched after EVERY save, Make default, and remove.
 * A stale list means an admin charging a card that no longer exists. Replace is the
 * deliberate exception: the `200` replace body is the authoritative post-state, so
 * this tab renders that response and does not issue a follow-up card-list read.
 *
 * **`locationId` is always sent on save.** A saved card BINDS to that location's payment
 * connection, so the card is bound to the service location the admin is working at — the
 * same one the tokenizer used. Relying on the home-location default is what later
 * produces a `422 SAVED_CARD_CONNECTION_MISMATCH` on a membership sale at another site.
 *
 * **Expiry is inferred here**, because the server deliberately does not hide expired
 * cards: a card is expired when its `expYear`/`expMonth` are in the past. A card missing
 * either field is **not** flagged — absence is never evidence of expiry — and an expired
 * card stays visible and removable so it can be cleaned up.
 *
 * **Per-card state gates the actions.** A card with `billsSubscriptions` is the derived
 * default card billing a live membership, so Remove is not rendered and a secondary
 * Replace action is offered. A card without that field can be made default or removed.
 * The billing note lives on the card it describes, not as a blanket line above the list.
 *
 * **Remove and Replace follow `canRemoveCards`** (DNKADM-0465, DNKAPI-0675): both are
 * ADMIN only on the server, so a STAFF session lists, adds and makes default but sees
 * neither button. The read fails closed (`useMoneyPermissions`: STAFF reads `false`
 * while it loads or if it fails).
 *
 * **Removal is money-adjacent and irreversible**, so it sits behind a confirm dialog that
 * names the card; dismissing it issues no request at all. The four documented outcomes
 * are handled distinctly: `204` removed, `404` already gone, `409 CARD_IN_USE` (a live
 * membership bills it) and `502` (processor unreachable, nothing removed). On the `409`
 * the server's `message` is shown **verbatim** as the server backstop.
 *
 * **Make default is additive.** The membership billing card changes to the clicked saved
 * card and the old card stays on file. Success refetches the wallet; no display-field
 * diffing is used to infer staff-made changes.
 *
 * **Replace is substitutive.** The replacement card takes over everything the old
 * card billed, and the old card is gone afterwards. Replace carries a required
 * idempotency key: the same key/logical operation is retried on transients, with a
 * fresh nonce for new-card retries; `422` keeps the dialog open but starts a fresh
 * attempt when the admin changes the credential; `404` refreshes the list;
 * `409 IDEMPOTENCY_IN_PROGRESS` is retryable and `409 IDEMPOTENCY_CONFLICT` is surfaced
 * without retry.
 *
 * White-label (§4.0): `cardBrand`/`maskedPan` are backend text, scrubbed at the render
 * boundary via `scrubBrand`. `cardId` is OPAQUE — never parsed, never displayed; the
 * accessible names identify the row by brand + masked pan, since a list carries many
 * identical buttons.
 *
 * **Sort (DNKADM-0430 P1-D).** The cards are card rows, not a table, so the shared
 * toolbar carries a `SortControl` (Added, Card, Expires), kept in the URL as
 * `pmSort`/`pmDir`. The default is Added, newest first: the server's own order. A card
 * missing the sorted field sorts last in both directions.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Icon, inkAlpha, resolveTokens } from '@/theme';
import { buildMembersMaps } from '@/theme/maps/members';
import { ApiError } from '@/api/errors';
import { v2ErrorCode } from '@/api/v2Errors';
import { serverMessage } from '@/api/serverMessage';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { useCurrentOperator } from '@/auth/authHooks';
import { useMoneyPermissions } from '@/auth/moneyPermissions';
import { ListToolbar } from '@/components/list/ListToolbar';
import { parseSort, sortRows, toSortParams } from '@/components/list/listSort';
import { SortControl } from '@/components/list/SortControl';
import { useListUrlState } from '@/components/list/useListUrlState';
import { useServiceLocation } from '@/location/useServiceLocation';
import { scrubBrand } from '@/utils/whitelabel';
import { cardSaveErrorMessage, cardVaultFailureMessage } from '@/utils/cardErrorMessage';
import {
  CARD_VERIFICATION_FAILED_MESSAGE,
  CardTokenizationError,
  type CardBuyer,
} from '@/api/payments';
import CardPaymentField, { type TokenizeFn } from '@/components/payments/CardPaymentField';
import {
  changeMemberCardBilling,
  listMemberCards,
  memberCardTransientRetry,
  removeMemberCard,
  replaceMemberCard,
  saveMemberCard,
  type MemberCard,
  type ReplaceCardInput,
  type ReplaceCardResult,
} from '@/api/memberCards';
import { getMemberMembership, type MemberMembership } from '@/api/members';
import { MEMBERS_FORBIDDEN_MESSAGE, MEMBERS_LOAD_FAILED_MESSAGE } from './membersError';
// The three display derivations are SHARED with the point-of-sale card-on-file chooser
// (DNKADM-0253), so an expired card reads the same on this tab and at the counter.
import { cardLabel, expiryLabel, isCardExpired } from './memberCardDisplay';
import {
  CARD_DEFAULT_SORT,
  CARD_SORT_KEYS,
  CARD_SORT_OPTIONS,
  PAYMENT_METHODS_URL_DEFAULTS,
  cardSortValue,
} from './memberDetailView';
import { ScreenError } from '@/components/ScreenError';
import { useCalendarZone } from '@/location/useCalendarZone';
import { DATE_ONLY, formatInstant } from '@/utils/instantFormat';

const PLACEHOLDER = '—';
const SKELETON_ROWS = 3;

/** How a removal ended, as far as the list is concerned. */
type RemoveOutcome = 'removed' | 'gone';

type ReplaceCredentialSource = 'new' | 'saved';

/**
 * "Added Jun 10, 2026" / raw string / placeholder — never throws. The day is the venue's
 * (`timeZone`, DNKADM-0476 F-05; null: the device).
 */
function addedLabel(createdAt: string | null, timeZone: string | null): string {
  if (createdAt === null) return PLACEHOLDER;
  return `Added ${formatInstant(createdAt, timeZone, DATE_ONLY)}`;
}

/**
 * A failed tokenize/save, as the admin sees it. Card-field failures get their own copy
 * (nothing was sent anywhere); everything else goes through `cardSaveErrorMessage`, which
 * turns a Square refusal into a plain line and otherwise shows dink's own message.
 */
function toCardErrorMessage(error: unknown, fallback?: string): string {
  if (error instanceof CardTokenizationError) {
    if (error.verification) return CARD_VERIFICATION_FAILED_MESSAGE;
    return error.validation
      ? 'Finish entering the card before saving.'
      : 'The card could not be read. Ask the member to check the card and try again.';
  }
  // fix-plain-card-errors: a Square refusal reads a plain line, never pay-api's sentence or code.
  return cardSaveErrorMessage(error, fallback);
}

function billingLabel(card: MemberCard, membership: MemberMembership | null): string | null {
  if (!cardBillsMembership(card)) return null;
  const planName = membership?.membershipPlanName ?? null;
  if (planName !== null) return `Billing your ${scrubBrand(planName)} membership`;
  return 'Billing an active membership';
}

function cardBillsMembership(card: MemberCard): boolean {
  return (card.billsSubscriptions?.length ?? 0) > 0;
}

function replaceCardInRows(
  rows: MemberCard[],
  oldCardId: string,
  replacement: MemberCard,
): MemberCard[] {
  const oldIndex = rows.findIndex((card) => card.cardId === oldCardId);
  if (oldIndex < 0) {
    return [
      replacement,
      ...rows.filter((card) => card.cardId !== oldCardId && card.cardId !== replacement.cardId),
    ];
  }
  const before = rows
    .slice(0, oldIndex)
    .filter((card) => card.cardId !== oldCardId && card.cardId !== replacement.cardId);
  const after = rows
    .slice(oldIndex + 1)
    .filter((card) => card.cardId !== oldCardId && card.cardId !== replacement.cardId);
  return [...before, replacement, ...after];
}

export function MemberPaymentMethodsTab({
  contactId,
  buyer,
}: {
  contactId: string;
  /** The member being served — the billing contact for the bank's card check (DNKADM-0487). */
  buyer: CardBuyer;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const showToast = useToast();
  const { canRemoveCards } = useMoneyPermissions();
  // The ONLY home of the card list: component state, for exactly as long as this tab is
  // mounted. Never lifted into a context/module/parent that would outlive the tab.
  const cards = useResource(
    useCallback(() => listMemberCards(contactId), [contactId]),
    [contactId],
  );
  const { refetch } = cards;
  // Context for the `409` — a COURTESY beside the list, never a gate on it: a failed
  // read simply omits the note and the cards still render.
  const membership = useResource(
    useCallback(() => getMemberMembership(contactId), [contactId]),
    [contactId],
  );
  const [addOpen, setAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<MemberCard | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<MemberCard | null>(null);
  const [makingDefaultCardId, setMakingDefaultCardId] = useState<string | null>(null);
  const [makeDefaultError, setMakeDefaultError] = useState<
    (ReplaceErrorView & { cardId: string }) | null
  >(null);
  const [displayedCards, setDisplayedCards] = useState<MemberCard[] | null>(null);

  useEffect(() => {
    setDisplayedCards(cards.data);
  }, [cards.data]);

  const handleSaved = useCallback(
    (reused: boolean) => {
      showToast(
        reused ? 'That card was already on file.' : 'Card saved. The member was not charged.',
      );
      setAddOpen(false);
      // Never cached: the list is server truth again immediately after the write.
      refetch();
    },
    [showToast, refetch],
  );

  const handleRemoved = useCallback(
    (outcome: RemoveOutcome) => {
      showToast(outcome === 'removed' ? 'Card removed.' : 'That card was already removed.');
      setRemoveTarget(null);
      refetch();
    },
    [showToast, refetch],
  );

  const handleMakeDefault = useCallback(
    async (card: MemberCard) => {
      if (makingDefaultCardId !== null) return;
      setMakingDefaultCardId(card.cardId);
      setMakeDefaultError(null);
      try {
        await changeMemberCardBilling(contactId, { cardId: card.cardId });
        showToast('Default card changed. The old card stayed on file.');
        refetch();
      } catch (err) {
        setMakeDefaultError({ ...toMakeDefaultErrorView(err), cardId: card.cardId });
      } finally {
        setMakingDefaultCardId(null);
      }
    },
    [contactId, makingDefaultCardId, refetch, showToast],
  );

  const handleReplaced = useCallback(
    (oldCardId: string, result: ReplaceCardResult) => {
      if (result.card === null) {
        setReplaceTarget(null);
        refetch();
        return;
      }
      const replacement = result.card;
      setDisplayedCards((current) =>
        replaceCardInRows(current ?? cards.data ?? [], oldCardId, replacement),
      );
    },
    [cards.data, refetch],
  );

  const handleReplaceGone = useCallback(() => {
    setReplaceTarget(null);
    refetch();
  }, [refetch]);

  const { values, set } = useListUrlState(PAYMENT_METHODS_URL_DEFAULTS);
  const sort = parseSort(values.pmSort, values.pmDir, CARD_SORT_KEYS, CARD_DEFAULT_SORT);
  // Equal values keep the server's order (newest first), so no tiebreak key.
  const rows = sortRows(
    displayedCards ?? cards.data ?? [],
    (card) => cardSortValue(card, sort.key),
    sort.dir,
  );
  const firstLoading = cards.status === 'loading' && cards.data === null;
  const now = new Date();

  return (
    <Box>
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body2" sx={{ color: t.text2 }}>
          Saved payment methods for this member. Saving a card never charges them.
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<Icon name="add" />}
          onClick={() => setAddOpen(true)}
        >
          Add card
        </Button>
      </Stack>

      {firstLoading && (
        <Paper elevation={1} sx={{ px: 3, py: 1 }} role="status" aria-label="Loading cards">
          {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
            <SkeletonRow key={`sk-${i}`} />
          ))}
        </Paper>
      )}

      {cards.status === 'error' && <CardsError error={cards.error} onRetry={refetch} />}

      {!firstLoading && cards.status !== 'error' && rows.length === 0 && <EmptyState />}

      {!firstLoading && cards.status !== 'error' && rows.length > 0 && (
        <ListToolbar
          filters={
            <SortControl
              sort={sort}
              options={CARD_SORT_OPTIONS}
              onChange={(next) => {
                const params = toSortParams(next, CARD_DEFAULT_SORT);
                set({ pmSort: params.sort, pmDir: params.dir });
              }}
            />
          }
        />
      )}

      {!firstLoading && cards.status !== 'error' && rows.length > 0 && (
        <Paper elevation={1} sx={{ px: 3, py: 1 }}>
          {rows.map((card, i) => (
            <CardRow
              key={card.cardId}
              card={card}
              last={i === rows.length - 1}
              expired={isCardExpired(card, now)}
              // The row's action is disabled while THAT card's removal is pending.
              busy={
                removeTarget?.cardId === card.cardId ||
                replaceTarget?.cardId === card.cardId ||
                makingDefaultCardId === card.cardId
              }
              makeDefaultError={makeDefaultError?.cardId === card.cardId ? makeDefaultError : null}
              billingLabel={billingLabel(card, membership.data)}
              canRemove={canRemoveCards}
              onMakeDefault={() => void handleMakeDefault(card)}
              onRetryMakeDefault={() => void handleMakeDefault(card)}
              onRemove={() => setRemoveTarget(card)}
              onReplace={() => setReplaceTarget(card)}
            />
          ))}
        </Paper>
      )}

      {addOpen && (
        <AddCardDialog
          contactId={contactId}
          buyer={buyer}
          onClose={() => setAddOpen(false)}
          onSaved={handleSaved}
        />
      )}

      {removeTarget !== null && (
        <RemoveCardDialog
          key={removeTarget.cardId}
          contactId={contactId}
          card={removeTarget}
          onClose={() => setRemoveTarget(null)}
          onRemoved={handleRemoved}
        />
      )}

      {replaceTarget !== null && (
        <ReplaceCardDialog
          key={replaceTarget.cardId}
          contactId={contactId}
          buyer={buyer}
          card={replaceTarget}
          cards={rows}
          onClose={() => setReplaceTarget(null)}
          onReplaced={handleReplaced}
          onGone={handleReplaceGone}
        />
      )}
    </Box>
  );
}

/**
 * One card on file. `cardId` is never rendered — the row is identified by brand +
 * masked pan, and that same derived label names the Remove button so a list of
 * otherwise-identical buttons stays distinguishable to a screen reader.
 */
function CardRow({
  card,
  last,
  expired,
  busy,
  makeDefaultError,
  billingLabel,
  canRemove,
  onMakeDefault,
  onRetryMakeDefault,
  onRemove,
  onReplace,
}: {
  card: MemberCard;
  last: boolean;
  expired: boolean;
  busy: boolean;
  makeDefaultError: ReplaceErrorView | null;
  billingLabel: string | null;
  /** Remove and Replace (ADMIN only on the server, DNKAPI-0675). */
  canRemove: boolean;
  onMakeDefault: () => void;
  onRetryMakeDefault: () => void;
  onRemove: () => void;
  onReplace: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const calendarZone = useCalendarZone();
  const label = cardLabel(card);
  const billing = cardBillsMembership(card);
  return (
    <Stack
      direction="row"
      sx={{
        gap: 1.75,
        py: 1.5,
        alignItems: 'center',
        flexWrap: 'wrap',
        borderBottom: last ? 'none' : `1px solid ${inkAlpha(theme, 0.05)}`,
      }}
    >
      <Avatar
        variant="rounded"
        sx={{ bgcolor: t.brandSoft, color: t.brand, width: 36, height: 36 }}
      >
        <Icon name="credit_card" sx={{ fontSize: 20 }} />
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{label}</Typography>
          {/* Expired cards are NOT hidden and stay removable — the chip is the cue to
              clean them up, not a reason to withhold the action. */}
          {expired && (
            <Chip size="small" color="warning" label="Expired" sx={{ height: 20, fontSize: 11 }} />
          )}
          {billing && (
            <Chip size="small" color="primary" label="Default" sx={{ height: 20, fontSize: 11 }} />
          )}
        </Stack>
        <Stack
          direction="row"
          sx={{ gap: 1.5, flexWrap: 'wrap', mt: 0.5, color: t.text2, fontSize: 12.5 }}
        >
          <Box component="span">Expires {expiryLabel(card)}</Box>
          <Box component="span">{addedLabel(card.createdAt, calendarZone)}</Box>
        </Stack>
        {billingLabel !== null && (
          <Typography variant="body2" sx={{ mt: 0.75, color: inkAlpha(theme, 0.72), fontSize: 12.5 }}>
            {billingLabel}
          </Typography>
        )}
      </Box>
      <Stack direction="row" sx={{ gap: 1, flexShrink: 0 }}>
        {billing ? (
          canRemove && (
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<Icon name="sync_alt" sx={{ fontSize: 17 }} />}
              aria-label={`Replace this card ${label}`}
              disabled={busy}
              onClick={onReplace}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              Replace this card
            </Button>
          )
        ) : (
          <>
            <Button
              size="small"
              variant="contained"
              color="inherit"
              startIcon={
                busy ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Icon name="check" sx={{ fontSize: 17 }} />
                )
              }
              aria-label={`Make default ${label}`}
              disabled={busy}
              onClick={onMakeDefault}
              sx={{ textTransform: 'none', flexShrink: 0 }}
            >
              {busy ? 'Making default…' : 'Make default'}
            </Button>
            {canRemove && (
              <Button
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<Icon name="delete" sx={{ fontSize: 17 }} />}
                aria-label={`Remove ${label}`}
                disabled={busy}
                onClick={onRemove}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                Remove
              </Button>
            )}
          </>
        )}
      </Stack>
      {makeDefaultError !== null && (
        <Alert
          severity="error"
          role="alert"
          sx={{ flexBasis: '100%', ml: { sm: 6.75 } }}
          action={
            makeDefaultError.retryable ? (
              <Button color="inherit" size="small" onClick={onRetryMakeDefault}>
                Try again
              </Button>
            ) : undefined
          }
        >
          {makeDefaultError.retryable && (
            <Typography component="p" variant="body2">
              We couldn&apos;t make this the default card. Try again.
            </Typography>
          )}
          <Typography component="p" variant="body2">
            {makeDefaultError.message}
          </Typography>
        </Alert>
      )}
    </Stack>
  );
}

/**
 * Add a card taken over the counter. The PAN/CVV are entered in Square's hosted field
 * and tokenized IN THE BROWSER — they never enter this component's state and never
 * reach a server; only the single-use nonce is posted, and it is not retained after the
 * request. `tenantSlug`/`locationRef` are resolved exactly as `SellCartDialog` does, and
 * when either is not ready the field is not rendered at all — the dialog explains why
 * instead of showing a broken card field.
 *
 * The resolved service location is ALSO what binds the saved card (`locationId`), so the
 * card is bound where the admin is working rather than to the member's home location.
 */
function AddCardDialog({
  contactId,
  buyer,
  onClose,
  onSaved,
}: {
  contactId: string;
  buyer: CardBuyer;
  onClose: () => void;
  onSaved: (reused: boolean) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const location = useServiceLocation();
  const operator = useCurrentOperator();
  const tenantSlug = operator?.tenantSlug?.trim() ?? '';
  const [cardReady, setCardReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const tokenizeRef = useRef<TokenizeFn | null>(null);

  const locationId = location.selectedId !== null ? Number(location.selectedId) : Number.NaN;
  const locationReady = location.status === 'ready' && Number.isFinite(locationId);
  const tenantReady = tenantSlug.length > 0;
  const canSubmit = locationReady && tenantReady && cardReady && !submitting;

  function close() {
    // A save in flight is un-dismissable: the nonce has already left the browser.
    if (submitting) return;
    onClose();
  }

  async function submit() {
    if (!canSubmit || tokenizeRef.current === null) return;
    setSubmitting(true);
    setError(null);
    try {
      // The nonce is a local, single-use value — never stored, logged, or kept in state.
      // DNKADM-0487: STORE — the bank may verify the card first; a cancelled or failed
      // check throws here, before anything is sent.
      const cardToken = await tokenizeRef.current({ intent: 'STORE', buyer });
      const saved = await saveMemberCard(contactId, cardToken, locationId);
      // `reused: true` means the card was already on file. That is a SUCCESS.
      onSaved(saved.reused);
    } catch (err) {
      setError(toCardErrorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth aria-labelledby="add-card-title">
      <DialogTitle
        id="add-card-title"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}
        >
          <Icon name="credit_card" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Add card</Typography>
          <Typography variant="body2">Saved for future purchases</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.7) }}>
            Saving this card does not charge the member — no payment is taken, no hold is placed,
            and nothing appears on their statement.
          </Typography>

          {locationReady && tenantReady ? (
            <CardPaymentField
              tenantSlug={tenantSlug}
              locationRef={location.selectedId ?? ''}
              disabled={submitting}
              onReady={(tokenize) => {
                tokenizeRef.current = tokenize;
                setCardReady(true);
              }}
              onError={() => {
                tokenizeRef.current = null;
                setCardReady(false);
              }}
            />
          ) : (
            <Alert severity="warning">
              {tenantReady
                ? 'Select a location before adding a card.'
                : 'Tenant payment context is unavailable. Sign in again before adding a card.'}
            </Alert>
          )}

          {error !== null && (
            <Alert severity="error" role="alert">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button
          color="inherit"
          onClick={close}
          disabled={submitting}
          sx={{ color: t.text2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name="check" />
          }
          onClick={() => void submit()}
          disabled={!canSubmit}
        >
          {submitting ? 'Saving…' : 'Save card'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

interface ReplaceErrorView {
  message: string;
  retryable: boolean;
}

type SuccessfulReplaceResult = ReplaceCardResult & { card: MemberCard };

function toMakeDefaultErrorView(error: unknown): ReplaceErrorView {
  const transient = memberCardTransientRetry(error);
  if (transient === 'card-retiring') {
    return {
      retryable: true,
      message: 'Another card change is already in progress. Try again shortly.',
    };
  }
  if (transient === 'processor-unavailable') {
    return {
      retryable: true,
      message: serverMessage(error) ?? 'The card processor is unavailable.',
    };
  }
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return {
        retryable: false,
        message: 'That membership or card was not found. Refresh and try again.',
      };
    }
    if (error.status === 422) {
      const code = v2ErrorCode(error);
      if (code === 'VALIDATION_FAILED') {
        return {
          retryable: false,
          message: serverMessage(error) ?? 'That card could not be made default.',
        };
      }
      if (code === 'PROVIDER_UNSUPPORTED') {
        return {
          retryable: false,
          message:
            serverMessage(error) ??
            'Only cards saved through this payment provider can bill this membership.',
        };
      }
      if (code === 'SAVED_CARD_CONNECTION_MISMATCH') {
        return {
          retryable: false,
          message:
            serverMessage(error) ??
            'That saved card is connected to a different payment account. Choose another saved card.',
        };
      }
      return {
        retryable: false,
        message: serverMessage(error) ?? 'The billing card could not be changed.',
      };
    }
  }
  return {
    retryable: false,
    message: serverMessage(error) ?? 'The billing card could not be changed.',
  };
}

function toReplaceErrorView(error: unknown): ReplaceErrorView {
  if (error instanceof CardTokenizationError) {
    return {
      retryable: false,
      message: toCardErrorMessage(error, 'The card could not be saved.'),
    };
  }
  const transient = memberCardTransientRetry(error);
  if (transient === 'card-retiring') {
    return {
      retryable: true,
      message: 'Another card change is already in progress. Try again shortly.',
    };
  }
  if (transient === 'idempotency-in-progress') {
    return {
      retryable: true,
      message: 'That replacement is still in progress. Try again shortly.',
    };
  }
  if (transient === 'processor-unavailable') {
    return {
      retryable: true,
      message: serverMessage(error) ?? 'The card processor is unavailable.',
    };
  }
  if (error instanceof ApiError) {
    if (error.status === 409 && v2ErrorCode(error) === 'IDEMPOTENCY_CONFLICT') {
      return {
        retryable: false,
        message: serverMessage(error) ?? 'The replace request conflicted with an earlier attempt.',
      };
    }
  }
  return { retryable: false, message: serverMessage(error) ?? 'The card could not be replaced.' };
}

function ReplaceCardDialog({
  contactId,
  buyer,
  card,
  cards,
  onClose,
  onReplaced,
  onGone,
}: {
  contactId: string;
  buyer: CardBuyer;
  card: MemberCard;
  cards: MemberCard[];
  onClose: () => void;
  onReplaced: (oldCardId: string, result: ReplaceCardResult) => void;
  onGone: () => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const location = useServiceLocation();
  const operator = useCurrentOperator();
  const tenantSlug = operator?.tenantSlug?.trim() ?? '';
  const otherCards = cards.filter((row) => row.cardId !== card.cardId);
  const [source, setSource] = useState<ReplaceCredentialSource>('new');
  const [savedCardId, setSavedCardId] = useState<string | null>(otherCards[0]?.cardId ?? null);
  const [cardReady, setCardReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ReplaceErrorView | null>(null);
  const [result, setResult] = useState<SuccessfulReplaceResult | null>(null);
  const tokenizeRef = useRef<TokenizeFn | null>(null);
  const attemptRef = useRef<ReplaceCardInput | null>(null);
  const label = cardLabel(card);

  const locationReady =
    location.status === 'ready' && (location.selectedId ?? '').trim().length > 0;
  const tenantReady = tenantSlug.length > 0;
  const savedReady = source === 'saved' && savedCardId !== null;
  const newCardReady = source === 'new' && locationReady && tenantReady && cardReady;
  const canSubmit = (savedReady || newCardReady || attemptRef.current !== null) && !submitting;

  function clearAttemptForEdit() {
    attemptRef.current = null;
    setError(null);
    setResult(null);
  }

  function close() {
    if (submitting) return;
    onClose();
  }

  function selectSource(next: ReplaceCredentialSource) {
    if (submitting) return;
    clearAttemptForEdit();
    setSource(next);
    tokenizeRef.current = null;
    setCardReady(false);
    if (next === 'saved' && savedCardId === null) {
      setSavedCardId(otherCards[0]?.cardId ?? null);
    }
  }

  function selectSavedCard(next: string) {
    if (submitting) return;
    clearAttemptForEdit();
    setSavedCardId(next);
  }

  async function buildInput(): Promise<ReplaceCardInput | null> {
    if (attemptRef.current !== null) {
      const attempt = attemptRef.current;
      if (typeof attempt.cardToken === 'string') {
        if (tokenizeRef.current === null) return null;
        const cardToken = await tokenizeRef.current({ intent: 'STORE', buyer });
        return { cardToken, idempotencyKey: attempt.idempotencyKey };
      }
      return attempt;
    }
    const idempotencyKey = crypto.randomUUID();
    if (source === 'saved') {
      if (savedCardId === null) return null;
      return { replacementCardId: savedCardId, idempotencyKey };
    }
    if (tokenizeRef.current === null) return null;
    // DNKADM-0487: a replacement card is stored to bill the membership — STORE.
    const cardToken = await tokenizeRef.current({ intent: 'STORE', buyer });
    return { cardToken, idempotencyKey };
  }

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const input = await buildInput();
      if (input === null) return;
      attemptRef.current = input;
      const replaced = await replaceMemberCard(contactId, card.cardId, input);
      attemptRef.current = null;
      onReplaced(card.cardId, replaced);
      if (replaced.card !== null) setResult({ ...replaced, card: replaced.card });
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        onGone();
        return;
      }
      if (err instanceof ApiError && err.status === 422) {
        attemptRef.current = null;
        setError({
          retryable: false,
          // fix-plain-card-errors: a vault refusal reads a plain line, never Square's code.
          message:
            cardVaultFailureMessage(err) ?? serverMessage(err) ?? 'The card could not be replaced.',
        });
        return;
      }
      const view = toReplaceErrorView(err);
      if (!view.retryable) attemptRef.current = null;
      setError(view);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth aria-labelledby="replace-card-title">
      <DialogTitle
        id="replace-card-title"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: t.brandSoft, color: t.brand, width: 38, height: 38 }}
        >
          <Icon name="sync_alt" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Replace card</Typography>
          <Typography variant="body2">{label}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {result === null ? (
          <Stack spacing={2}>
            <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.7) }}>
              The replacement card takes over this card’s memberships. The old card is removed
              afterward.
            </Typography>

            {otherCards.length > 0 && (
              <RadioGroup
                aria-label="Replacement source"
                value={source}
                aria-disabled={submitting || undefined}
                onChange={(event) => selectSource(event.target.value as ReplaceCredentialSource)}
              >
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label="Enter a new card"
                  disabled={submitting}
                />
                <FormControlLabel
                  value="saved"
                  control={<Radio />}
                  label="Use a saved card"
                  disabled={submitting}
                />
              </RadioGroup>
            )}

            {source === 'new' ? (
              locationReady && tenantReady ? (
                <CardPaymentField
                  tenantSlug={tenantSlug}
                  locationRef={location.selectedId ?? ''}
                  disabled={submitting}
                  onReady={(tokenize) => {
                    tokenizeRef.current = tokenize;
                    setCardReady(true);
                  }}
                  onError={() => {
                    tokenizeRef.current = null;
                    setCardReady(false);
                  }}
                />
              ) : (
                <Alert severity="warning">
                  {tenantReady
                    ? 'Select a location before replacing a card.'
                    : 'Tenant payment context is unavailable. Sign in again before replacing a card.'}
                </Alert>
              )
            ) : (
              <RadioGroup
                aria-label="Saved replacement card"
                value={savedCardId ?? ''}
                aria-disabled={submitting || undefined}
                onChange={(event) => selectSavedCard(event.target.value)}
              >
                {otherCards.map((other) => (
                  <FormControlLabel
                    key={other.cardId}
                    value={other.cardId}
                    control={<Radio />}
                    label={cardLabel(other)}
                    disabled={submitting}
                  />
                ))}
              </RadioGroup>
            )}

            {error !== null && (
              <Alert
                severity="error"
                role="alert"
                action={
                  error.retryable ? (
                    <Button color="inherit" size="small" onClick={() => void submit()}>
                      Try again
                    </Button>
                  ) : undefined
                }
              >
                {error.retryable && (
                  <Typography component="p" variant="body2">
                    We couldn&apos;t finish — nothing was removed. Try again.
                  </Typography>
                )}
                <Typography component="p" variant="body2">
                  {error.message}
                </Typography>
              </Alert>
            )}
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Alert severity="success" role="status">
              Card saved. The old card is gone.
            </Alert>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {cardLabel(result.card)}
              </Typography>
              <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.65), mt: 0.5 }}>
                {result.movedSubscriptionIds.length > 0
                  ? 'Membership billing moved to this card.'
                  : 'This card is now saved for the member.'}
              </Typography>
            </Box>
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        {result === null ? (
          <>
            <Button
              color="inherit"
              onClick={close}
              disabled={submitting}
              sx={{ color: t.text2 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={
                submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name="check" />
              }
              onClick={() => void submit()}
              disabled={!canSubmit}
            >
              {submitting ? 'Replacing…' : 'Replace card'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={close}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

/** A failed removal, as the admin sees it. */
interface RemoveErrorView {
  message: string;
  retryable: boolean;
}

/**
 * Map a failed removal. The `409 CARD_IN_USE` branch surfaces the server's `message`
 * **VERBATIM** (through the `serverMessage` scrub seam) — it already names the blocking
 * subscription — and deliberately adds no route: this app has no membership
 * payment-method screen to send the admin to. A `502` is the only retryable outcome:
 * the processor was unreachable, so nothing was removed.
 */
function toRemoveErrorView(error: unknown): RemoveErrorView {
  if (error instanceof ApiError) {
    if (error.status === 409 && v2ErrorCode(error) === 'CARD_IN_USE') {
      return {
        retryable: false,
        message:
          serverMessage(error) ?? 'This card is billing a live membership and cannot be removed.',
      };
    }
    if (error.status === 502) {
      return {
        retryable: true,
        message: 'The card processor is unavailable. Nothing was removed.',
      };
    }
  }
  return { retryable: false, message: serverMessage(error) ?? 'The card could not be removed.' };
}

/**
 * The removal confirm step — mandatory, because removing a card on file is
 * money-adjacent and irreversible. Dismissing it issues NO request; only Remove calls
 * the endpoint. The dialog cannot be dismissed while a removal is in flight.
 *
 * Outcomes: `204` → removed; `404` → already gone (reported as a success to the parent,
 * which refetches — never an error); `409 CARD_IN_USE` → the server message verbatim,
 * dialog stays open, the card stays in the list; `502` → nothing removed, with a Retry.
 */
function RemoveCardDialog({
  contactId,
  card,
  onClose,
  onRemoved,
}: {
  contactId: string;
  card: MemberCard;
  onClose: () => void;
  onRemoved: (outcome: RemoveOutcome) => void;
}) {
  const theme = useTheme();
  const t = resolveTokens(theme);
  const members = buildMembersMaps(theme.palette.mode === 'dark' ? 'dark' : 'light');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<RemoveErrorView | null>(null);
  // Guards a synchronous double-click on Remove: `disabled` only lands on the next
  // render, so the ref blocks the second handler in the same tick.
  const inFlight = useRef(false);
  const label = cardLabel(card);

  function close() {
    if (submitting) return;
    onClose();
  }

  async function confirm() {
    if (inFlight.current) return;
    inFlight.current = true;
    setSubmitting(true);
    setError(null);
    try {
      await removeMemberCard(contactId, card.cardId);
      onRemoved('removed');
    } catch (err) {
      // Gone, or not this member's — the list is simply out of date, not an error.
      if (err instanceof ApiError && err.status === 404) {
        onRemoved('gone');
        return;
      }
      setError(toRemoveErrorView(err));
      setSubmitting(false);
      inFlight.current = false;
    }
  }

  return (
    <Dialog open onClose={close} maxWidth="xs" fullWidth aria-labelledby="remove-card-title">
      <DialogTitle
        id="remove-card-title"
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, pb: 0.5 }}
      >
        <Avatar
          variant="rounded"
          sx={{ bgcolor: members.dangerAlt.bg, color: members.dangerAlt.fg, width: 38, height: 38 }}
        >
          <Icon name="delete" sx={{ fontSize: 21 }} />
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Remove card</Typography>
          <Typography variant="body2">{label}</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: inkAlpha(theme, 0.7) }}>
          This card is removed from the member’s file and can’t be used for future purchases. It
          can’t be undone — the member would have to present the card again.
        </Typography>
        {error !== null && (
          <Alert
            severity="error"
            role="alert"
            sx={{ mt: 2 }}
            action={
              error.retryable ? (
                <Button color="inherit" size="small" onClick={() => void confirm()}>
                  Retry
                </Button>
              ) : undefined
            }
          >
            {error.message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${t.lineSoft}`, px: 2.5, py: 1.5 }}>
        <Button
          color="inherit"
          onClick={close}
          disabled={submitting}
          sx={{ color: t.text2 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : <Icon name="delete" />
          }
          onClick={() => void confirm()}
          disabled={submitting}
        >
          {submitting ? 'Removing…' : 'Remove'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SkeletonRow() {
  return (
    <Stack direction="row" sx={{ gap: 1.75, py: 1.875, alignItems: 'center' }}>
      <Skeleton variant="rounded" width={36} height={36} />
      <Box sx={{ flex: 1 }}>
        <Skeleton width={180} height={14} />
        <Skeleton width={220} height={12} sx={{ mt: 0.5 }} />
      </Box>
      <Skeleton variant="rounded" width={92} height={30} />
    </Stack>
  );
}

function EmptyState() {
  const theme = useTheme();
  return (
    <Box sx={{ py: 6, textAlign: 'center' }}>
      <Icon name="credit_card" sx={{ fontSize: 42, color: inkAlpha(theme, 0.2) }} />
      <Typography sx={{ fontSize: 15, fontWeight: 500, mt: 1 }}>
        No cards on file for this member.
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5, maxWidth: 340, mx: 'auto' }}>
        Add a card the member presents at the counter — saving it doesn’t charge them.
      </Typography>
    </Box>
  );
}

function CardsError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <ScreenError
      error={error}
      what="the cards on file"
      onRetry={onRetry}
      forbiddenMessage={MEMBERS_FORBIDDEN_MESSAGE}
      retryMessage={MEMBERS_LOAD_FAILED_MESSAGE}
      preferServerMessage
      sx={{ alignItems: 'center' }}
    />
  );
}
